import React, { useState, useEffect } from "react";
import { useOrganisations } from "../hooks/useOrganisations";
import { useOrganisationDetail } from "../hooks/useOrganisations";
import { useBulkManageRoles } from "../hooks/useBulkRoles";
import { useRoles } from "../hooks/useRoles";
import { Spinner } from "../components/ui/Spinner";
import { Button } from "../components/ui/Button";
import { Badge } from "../components/ui/Badge";
import { RoleSelector } from "../components/ui/RoleSelector";
import type { OrgSummary, OrgUser, Role } from "../domain/types/admin.types";

const SYSTEM_ROLES = new Set(["ROLE_ADMIN", "ROLE_USER"]);

export const BulkRolesPage: React.FC = () => {
  const [selectedOrgId, setSelectedOrgId] = useState("");
  const [selectAll, setSelectAll] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [action, setAction] = useState<"add" | "remove">("add");
  const [result, setResult] = useState<{ affected: number; auditLogId: string } | null>(null);
  const [submitError, setSubmitError] = useState("");

  const orgsQuery = useOrganisations(1, 100, "");
  const detailQuery = useOrganisationDetail(selectedOrgId);
  const rolesQuery = useRoles();
  const bulkMutation = useBulkManageRoles();

  const orgs = orgsQuery.data?.organisations ?? [];
  const orgUsers: OrgUser[] = detailQuery.data?.users ?? [];
  const allRoles: Role[] = rolesQuery.data ?? [];
  const assignableRoles = allRoles.filter((r) => !SYSTEM_ROLES.has(r.name));

  useEffect(() => {
    setSelectAll(false);
    setSelectedUserIds(new Set());
    setResult(null);
    setSubmitError("");
  }, [selectedOrgId]);

  const toggleUser = (id: string) => {
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSelectAll = (checked: boolean) => {
    setSelectAll(checked);
    if (checked) setSelectedUserIds(new Set(orgUsers.map((u) => u.id)));
    else setSelectedUserIds(new Set());
  };

  const handleSubmit = async () => {
    setSubmitError("");
    setResult(null);

    if (!selectedOrgId) return setSubmitError("Select an organisation.");
    const userIds = selectAll ? orgUsers.map((u) => u.id) : Array.from(selectedUserIds);
    if (userIds.length === 0) return setSubmitError("Select at least one user.");
    if (selectedRoles.length === 0) return setSubmitError("Select at least one role.");

    try {
      const res = await bulkMutation.mutateAsync({
        orgId: selectedOrgId,
        userIds,
        roles: selectedRoles,
        action,
      });
      setResult(res);
      setSelectedUserIds(new Set());
      setSelectAll(false);
      setSelectedRoles([]);
    } catch (err: any) {
      setSubmitError(err?.data?.message ?? "Operation failed.");
    }
  };

  const effectiveUserCount = selectAll ? orgUsers.length : selectedUserIds.size;

  return (
    <div className="flex flex-col gap-6 max-w-3xl">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Bulk Role Management</h2>
        <p className="text-xs text-gray-400 mt-0.5">
          Add or remove roles for multiple users at once. All changes are audit-logged.
        </p>
      </div>

      {/* Step 1: Organisation */}
      <Section label="1. Select Organisation">
        {orgsQuery.isLoading ? (
          <div className="flex items-center gap-2 text-sm text-gray-400">
            <Spinner className="h-4 w-4" /> Loading…
          </div>
        ) : (
          <select
            value={selectedOrgId}
            onChange={(e) => setSelectedOrgId(e.target.value)}
            className="h-10 w-full max-w-sm rounded border border-border-gray bg-white px-3 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-blue"
          >
            <option value="">— Choose organisation —</option>
            {orgs.map((org: OrgSummary) => (
              <option key={org.id} value={org.id}>
                {org.name}
              </option>
            ))}
          </select>
        )}
      </Section>

      {/* Step 2: Users */}
      {selectedOrgId && (
        <Section label="2. Select Users">
          {detailQuery.isLoading ? (
            <div className="flex items-center gap-2 text-sm text-gray-400">
              <Spinner className="h-4 w-4" /> Loading users…
            </div>
          ) : orgUsers.length === 0 ? (
            <p className="text-sm text-gray-400">No users in this organisation.</p>
          ) : (
            <div className="flex flex-col gap-1">
              <label className="flex items-center gap-3 px-3 py-2.5 rounded border border-primary-blue bg-blue-50 cursor-pointer select-none">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-primary-blue"
                  checked={selectAll}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                />
                <span className="text-sm font-medium text-gray-800">
                  All users in this organisation ({orgUsers.length})
                </span>
              </label>

              <div className="border border-border-gray rounded divide-y divide-border-gray mt-1">
                {orgUsers.map((user: OrgUser) => {
                  const checked = selectAll || selectedUserIds.has(user.id);
                  return (
                    <label
                      key={user.id}
                      className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer select-none transition-colors ${
                        checked && !selectAll ? "bg-blue-50" : "hover:bg-default-99"
                      } ${selectAll ? "opacity-50 pointer-events-none" : ""}`}
                    >
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-primary-blue"
                        checked={checked}
                        onChange={() => toggleUser(user.id)}
                        disabled={selectAll}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-800 truncate">
                          {user.firstName} {user.lastName}
                        </p>
                        <p className="text-xs text-gray-400 truncate">{user.email}</p>
                      </div>
                      <div className="flex flex-wrap gap-1 justify-end max-w-xs max-h-16 overflow-y-auto">
                        {[...user.roles].sort().map((r) => (
                          <Badge key={r} variant="default">
                            {r.replace("ROLE_", "")}
                          </Badge>
                        ))}
                      </div>
                    </label>
                  );
                })}
              </div>

              {effectiveUserCount > 0 && (
                <p className="text-xs text-gray-400 mt-1">
                  {effectiveUserCount} user{effectiveUserCount !== 1 ? "s" : ""} selected
                </p>
              )}
            </div>
          )}
        </Section>
      )}

      {/* Step 3: Roles */}
      {selectedOrgId && (
        <Section label="3. Select Roles">
          {rolesQuery.isLoading ? (
            <div className="flex items-center gap-2 text-sm text-gray-400">
              <Spinner className="h-4 w-4" /> Loading roles…
            </div>
          ) : assignableRoles.length === 0 ? (
            <p className="text-sm text-gray-400">No assignable roles.</p>
          ) : (
            <RoleSelector
              roles={assignableRoles}
              selected={selectedRoles}
              onChange={setSelectedRoles}
              maxHeight="max-h-72"
            />
          )}
        </Section>
      )}

      {/* Step 4: Action + Submit */}
      {selectedOrgId && (
        <Section label="4. Action">
          <div className="flex gap-4">
            <ActionRadio
              value="add"
              current={action}
              onChange={setAction}
              label="Add roles"
              description="Grant selected roles to selected users"
            />
            <ActionRadio
              value="remove"
              current={action}
              onChange={setAction}
              label="Remove roles"
              description="Revoke selected roles from selected users"
            />
          </div>

          {submitError && <p className="text-xs text-red-500 mt-3">{submitError}</p>}

          {result && (
            <div className="mt-3 rounded border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
              <p className="font-medium">
                Done — {result.affected} user{result.affected !== 1 ? "s" : ""} updated.
              </p>
              <p className="text-xs text-green-500 mt-0.5">Audit log: {result.auditLogId}</p>
            </div>
          )}

          <div className="mt-4">
            <Button
              onClick={handleSubmit}
              disabled={bulkMutation.isPending}
              variant={action === "remove" ? "outline" : "default"}
              className={`w-full whitespace-nowrap ${action === "remove" ? "border-red-300 text-red-600 hover:bg-red-50" : ""}`}
            >
              {bulkMutation.isPending ? (
                <span className="flex items-center gap-2">
                  <Spinner className="h-4 w-4 border-white border-r-transparent" />
                  Processing…
                </span>
              ) : action === "add" ? (
                `Add roles to ${effectiveUserCount} user${effectiveUserCount !== 1 ? "s" : ""}`
              ) : (
                `Remove roles from ${effectiveUserCount} user${effectiveUserCount !== 1 ? "s" : ""}`
              )}
            </Button>
          </div>
        </Section>
      )}
    </div>
  );
};

const Section: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="bg-white rounded border border-border-gray">
    <div className="px-4 py-3 border-b border-border-gray">
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{label}</p>
    </div>
    <div className="px-4 py-4">{children}</div>
  </div>
);

const ActionRadio: React.FC<{
  value: "add" | "remove";
  current: "add" | "remove";
  onChange: (v: "add" | "remove") => void;
  label: string;
  description: string;
}> = ({ value, current, onChange, label, description }) => {
  const active = value === current;
  return (
    <label
      className={`flex-1 flex items-start gap-3 rounded border px-4 py-3 cursor-pointer transition-colors ${
        active
          ? value === "add"
            ? "border-primary-blue bg-blue-50"
            : "border-red-300 bg-red-50"
          : "border-border-gray hover:bg-default-99"
      }`}
    >
      <input
        type="radio"
        name="bulk-action"
        value={value}
        checked={active}
        onChange={() => onChange(value)}
        className="mt-0.5 accent-primary-blue"
      />
      <div>
        <p className={`text-sm font-medium ${active && value === "remove" ? "text-red-600" : "text-gray-800"}`}>
          {label}
        </p>
        <p className="text-xs text-gray-400 mt-0.5">{description}</p>
      </div>
    </label>
  );
};
